import { Button, EmptyState, ErrorState, LoadingState, Panel } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { useAddStock, useInvestmentsPage, useRecordTransaction } from "../hooks.js";
import { InvestmentsStats } from "./InvestmentsStats.js";
import { PortfolioChart } from "./PortfolioChart.js";
import { PositionsTable } from "./PositionsTable.js";
import { RangeControls } from "./RangeControls.js";
import { StockForm } from "./StockForm.js";

/**
 * The Investments page.
 *
 * ==========================================================================================
 * THIS PAGE COMPOSES NOTHING
 * ==========================================================================================
 *
 * It reads one query and renders it. Net worth, income against outgoings, any comparison with
 * another pillar — all of that is the Dashboard's (013). `investments-composes-nothing.test.ts`
 * enforces it, because the dependency rules do not: `apps/app` may legitimately import several domain
 * `/ui` packages, so nothing stops this file importing savings except a test that says not to.
 *
 * CLOSED POSITIONS ARE A COLLAPSED SECTION with a count, not a toggle that merges them into the table
 * above. A household with many closed positions should not scroll past them to reach what it owns,
 * and the count is what makes the section discoverable while collapsed.
 *
 * ==========================================================================================
 * THE ADD DIALOG IS CONTROLLED FROM THE ROUTE
 * ==========================================================================================
 *
 * Because the button that opens it is not here. Every page's primary action sits at the end of its
 * title row, in `Page.Title`'s `actions` slot, and that row belongs to the route. This page held the
 * open/closed state itself and offered `Add a stock` only inside its empty state — so the control
 * disappeared the moment the household owned one stock, and there was no way back to it.
 */

export interface InvestmentsPageProps {
  onOpenStock: (stockId: string) => void;
  /** Whether the add-stock dialog is open. Owned by the route — see the note above. */
  adding: boolean;
  onAddingChange: (adding: boolean) => void;
}

export function InvestmentsPage({
  onOpenStock,
  adding,
  onAddingChange,
}: InvestmentsPageProps): ReactNode {
  const t = useT();
  const [months, setMonths] = useState(12);
  const page = useInvestmentsPage(months);
  const addStock = useAddStock();
  const recordTransaction = useRecordTransaction();
  const [closedOpen, setClosedOpen] = useState(false);

  /*
   * Adding a stock with an opening purchase is TWO calls, so it has a state between them.
   *
   * If the transaction fails, the stock is already created — and submitting the dialog again would
   * otherwise add a SECOND stock with the same symbol. Holding the created id means a retry records
   * only the purchase, against the stock that already exists.
   */
  const [createdStockId, setCreatedStockId] = useState<string | null>(null);

  function closeAdding(): void {
    onAddingChange(false);
    setCreatedStockId(null);
    addStock.reset();
    recordTransaction.reset();
  }

  if (page.isPending) return <LoadingState>{t("inv.page.loading")}</LoadingState>;
  if (page.isError) return <ErrorState>{t("inv.page.loadFailed")}</ErrorState>;

  const data = page.data;
  const nothingYet = data.activePositions.length === 0 && data.closedPositions.length === 0;

  /* The stock landed and only the purchase did not — say which, or the member re-types both. */
  const addError =
    addStock.error?.message ??
    (recordTransaction.error
      ? `The stock was added, but the purchase was not recorded: ${errorSentence(recordTransaction.error)} Submitting again records the purchase only.`
      : null);

  // Mounted only while open — see StockDetail for why a persistent dialog carries stale fields.
  const form = adding ? (
    <StockForm
      open
      onClose={closeAdding}
      editing={null}
      pending={addStock.isPending || recordTransaction.isPending}
      error={addError}
      onSubmit={(input) => {
        function recordFirstBuy(stockId: string): void {
          if (input.firstBuy === null) {
            closeAdding();
            return;
          }
          recordTransaction.mutate(
            { stockId, kind: "buy", ...input.firstBuy },
            { onSuccess: closeAdding },
          );
        }

        if (createdStockId !== null) {
          recordFirstBuy(createdStockId);
          return;
        }

        addStock.mutate(
          { name: input.name, symbol: input.symbol, exchange: input.exchange },
          {
            onSuccess: (stock) => {
              setCreatedStockId(stock.id);
              recordFirstBuy(stock.id);
            },
          },
        );
      }}
    />
  ) : null;

  /*
   * A household with nothing sees one prompt, not four cards reading €0.00 as though that were a
   * finding about its portfolio.
   *
   * NO BUTTON HERE, and that is the house pattern rather than an omission. `AccountList`, `CardSet`
   * and `ExpenseList` all state what is missing and stop; the control to fix it is the page's primary
   * action, in the title row, where it stays whether the household owns nothing or twenty holdings.
   * A second copy here would be the same action in two places, and — because both would be reachable
   * at once — genuinely ambiguous to anything selecting it by name.
   */
  if (nothingYet) {
    return (
      <>
        <EmptyState>
          <p>{t("inv.page.empty")}</p>
          <p className="mt-2 text-sm">{t("inv.page.emptyHint")}</p>
        </EmptyState>
        {form}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <InvestmentsStats page={data} />

      <Panel
        title={t("inv.page.portfolioValue")}
        description={t("inv.page.chartHint")}
        aside={<RangeControls months={months} onChange={setMonths} />}
      >
        <PortfolioChart page={data} />
      </Panel>

      <Panel title={t("inv.page.heading")} description={t("inv.page.hint")}>
        <PositionsTable positions={data.activePositions} onOpen={onOpenStock} />
      </Panel>

      {data.closedPositions.length > 0 && (
        <Panel
          title={t("inv.page.closed")}
          description={t("inv.page.closedHint")}
          aside={
            <Button
              onClick={() => {
                setClosedOpen((open) => !open);
              }}
              aria-expanded={closedOpen}
            >
              <span aria-hidden="true" className="mr-2">
                {closedOpen ? "▾" : "▸"}
              </span>
              {closedOpen ? t("inv.page.hide") : `Show ${String(data.closedPositions.length)}`}
            </Button>
          }
        >
          {closedOpen ? (
            <PositionsTable
              positions={data.closedPositions}
              variant="closed"
              onOpen={onOpenStock}
            />
          ) : (
            <p className="text-muted text-sm">
              {data.closedPositions.length}{" "}
              {data.closedPositions.length === 1 ? "holding has" : "holdings have"} been sold in
              full.
            </p>
          )}
        </Panel>
      )}

      {form}
    </div>
  );
}

/** The page's primary action, for `Page.Title`'s `actions` slot. */
export function AddStockAction({ onClick }: { onClick: () => void }): ReactNode {
  const t = useT();
  return (
    <Button variant="primary" onClick={onClick}>
      {t("inv.page.addStock")}
    </Button>
  );
}
